import {
  LocalDateTimeString,
  LocalTimeString,
  Workday,
  WorkspaceReservation,
} from '@lib/store/src/data/timeslots/apiTypes';
import {createWorkdayPatch, WorkdayPatch} from '@lib/store/src/data/timeslots/trimming';
import {StyledModalPage} from './styles';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useModal} from '@hooks';
import {TimeSlotsTimePicker, Button, ModalCell} from '@molecules';
import {useTranslation} from 'react-i18next';
import {dateKeyForDateTimeString, localDateTimeStringToHHMM} from '@lib/store/src/data/timeslots/dateTimeHelpers';
import {DaySchedule} from '../../ganttChart/DaySchedule';
import {MAX_TIMESPAN_PRESET, MIN_TIMESPAN_PRESET} from '@lib/store/src/data/timeslots/constants/startAndEndTimes';
import {ScheduleMutation, useDaySchedule} from '@lib/store/src/ui/hooks/timeslots/useDaySchedule';
import {useCleanupWorkday} from './hooks/useCleanupWorkday';
import {isOfficeWorkday} from '@lib/store/src/data/timeslots/utils';
import {TimeAdjustmentSuggestion} from './TimeAdjustmentSuggestion';
import {useOverridableFeatureFlags} from '@lib/store';

type LocalTimespan = {
  localStart: LocalDateTimeString;
  localEnd: LocalDateTimeString;
};

const useKeyBasedForcedRemount = () => {
  const [idx, setIdx] = useState(0);

  return [idx, () => setIdx((prev) => prev + 1)] as const;
};

export const EditTimeModal = ({
  workspaceReservation,
  workday,

  defaultTimespan,
  onConfirm,
  onCancel,
}: {
  workspaceReservation?: WorkspaceReservation;
  workday: Workday;

  defaultTimespan: LocalTimespan;
  onConfirm: (startTime: LocalTimeString, endTime: LocalTimeString, workdayPatch?: WorkdayPatch) => void;
  onCancel: () => void;
}) => {
  const {t} = useTranslation();
  const {closeModal, removePages, setOnCloseCallback} = useModal();
  const features = useOverridableFeatureFlags();

  const [internalTimespan, setInternalTimespan] = useState(defaultTimespan);
  const [timepickerKey, remountTimePicker] = useKeyBasedForcedRemount();

  const dateKey = dateKeyForDateTimeString(internalTimespan.localStart);
  const startHhmm = localDateTimeStringToHHMM(internalTimespan.localStart);
  const endHhmm = localDateTimeStringToHHMM(internalTimespan.localEnd);

  const mutation = useMemo<ScheduleMutation>(() => {
    return {
      type: workspaceReservation ? 'EDIT' : 'CREATE',
      entityType: 'WORKSPACE_RESERVATION',
      entity: {
        id: workspaceReservation?.id ?? 'DRAFT',
        localStartTime: internalTimespan.localStart,
        localEndTime: internalTimespan.localEnd,
        workdayId: workday.id,
      },
    };
  }, [workspaceReservation, internalTimespan, workday.id]);

  const vm = useDaySchedule(dateKey, mutation);

  const {cleanUpWorkdayIfRequired} = useCleanupWorkday({
    workday,
    workspaceReservation,
  });

  const handleAcceptSuggestion = (suggestion: LocalTimespan) => {
    setInternalTimespan(suggestion);
    // Time picker does not yet support reactive values, so we
    // remount it to ensure it reflects our changed timestamp
    remountTimePicker();
  };

  const onClose = useCallback(() => {
    cleanUpWorkdayIfRequired();
    closeModal();
  }, [cleanUpWorkdayIfRequired, closeModal]);

  // Override onClose callback to include workday cleanup
  useEffect(() => {
    setOnCloseCallback(() => () => onClose());

    return () => {
      setOnCloseCallback(false);
    };
    // Prevent infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setOnCloseCallback]);

  return (
    <StyledModalPage
      title={'Select time'}
      onBack={() => removePages(1)}
      bottom={
        <div className="grid grid-cols-2 gap-5 px-5">
          <Button
            button="secondary"
            onClick={onCancel}>
            {t('translation:Cancel')}
          </Button>
          <Button
            button="primary"
            loading={false}
            onClick={() => {
              const workdayPatch = createWorkdayPatch(workday, internalTimespan);

              onConfirm(internalTimespan.localStart, internalTimespan.localEnd, workdayPatch);
            }}>
            {t('translation:Save')}
          </Button>
        </div>
      }
      onClose={onClose}>
      <ModalCell>
        <div className="flex items-center gap-2">
          <span className="mr-auto">Time</span>
          <TimeSlotsTimePicker
            key={`start-${timepickerKey}`}
            defaultValue={startHhmm}
            onChange={(v) => {
              const next = v || '00:00';
              setInternalTimespan((prev) => ({
                ...prev,
                localStart: `${dateKey}T${next}`,
              }));
            }}
            hasError={(vm.schedule && vm.schedule.validationIssues.length > 0) || startHhmm >= endHhmm}
            presetTransformer={(o) => ({
              ...o,
              hidden: o.id < MIN_TIMESPAN_PRESET || o.id > MAX_TIMESPAN_PRESET,
            })}
          />
          -
          <TimeSlotsTimePicker
            key={`end-${timepickerKey}`}
            defaultValue={endHhmm}
            onChange={(v) =>
              setInternalTimespan((prev) => ({
                ...prev,
                localEnd: `${dateKey}T${v}`,
              }))
            }
            hasError={(vm.schedule && vm.schedule.validationIssues.length > 0) || endHhmm <= startHhmm}
            presetTransformer={(o) => {
              return {
                ...o,
                hidden: o.id < MIN_TIMESPAN_PRESET || o.id > MAX_TIMESPAN_PRESET || (!!startHhmm && o.id <= startHhmm),
              };
            }}
          />
        </div>
      </ModalCell>

      <div className="flex-grow-1 p-4">
        <div className="bg-white border-1 rounded-xl p-4">
          <h4 className="mb-4">Your schedule</h4>
          <DaySchedule
            vm={vm}
            mutation={mutation}
            extraArgs={{
              buildingName: isOfficeWorkday(workday)
                ? workday.buildingName
                : workspaceReservation?.workspace.buildingName,
            }}
          />
        </div>
      </div>

      {features.TimeslotsAdjustmentSuggestion && (
        <TimeAdjustmentSuggestion
          currentTimespan={internalTimespan}
          defaultTimespan={defaultTimespan}
          hidden={Boolean(vm.schedule?.validationIssues?.length)}
          mutationType={mutation.type === 'EDIT' ? 'edit' : 'create'}
          onAcceptSuggestion={handleAcceptSuggestion}
        />
      )}
    </StyledModalPage>
  );
};
